// src/pages/auth/SignupPage.tsx
import { useEffect, useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Card from '../../components/base/Card'
import Input from '../../components/base/Input'
import Button from '../../components/base/Button'
import RotatingMessage from './RotateMessage'
import TurnstileGate from '../../components/TurnstileGate'
import type { TurnstileGateHandle } from '../../components/TurnstileGate'
import { LS_EMAIL_KEY } from '../../lib/constants'

export default function SignupPage() {
  const navigate = useNavigate()
  const gateRef = useRef<TurnstileGateHandle>(null)
  const [token, setToken] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)
  const [apiSuccess, setApiSuccess] = useState<string | null>(null)

  // ✅ 1) 최초 로딩 시 localStorage 이메일 불러오기
  useEffect(() => {
    try {
      const saved = localStorage.getItem(LS_EMAIL_KEY)
      if (saved) {
        setFormData((prev) => ({ ...prev, email: saved }))
      }
    } catch {
      // localStorage 사용 불가 환경이면 무시
    }
  }, [])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = '이름을 입력해주세요'
    }

    if (!formData.email.trim()) {
      newErrors.email = '이메일을 입력해주세요'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다'
    }

    if (!formData.password) {
      newErrors.password = '비밀번호를 입력해주세요'
    } else if (formData.password.length < 8) {
      newErrors.password = '비밀번호는 최소 8자 이상이어야 합니다'
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = '비밀번호가 일치하지 않습니다'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    if (!token) {
      setApiError('봇 검증을 완료해 주세요.')
      gateRef.current?.show() // (선택) 혹시 숨겨져 있으면 보여주기
      return
    }

    setLoading(true)
    setApiError(null)
    setApiSuccess(null)

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          turnstileToken: token
        })
      })

      if (!response.ok) {
        // 기본 에러 메시지
        let errorMessage = '회원가입 중 오류가 발생했습니다'

        // ✅ 409: 이메일 중복 처리
        if (response.status === 409) {
          errorMessage = '이미 등록된 이메일입니다. 다른 이메일을 사용해주세요.'

          // 이메일 필드에 에러 표시
          setErrors((prev) => ({
            ...prev,
            email: errorMessage
          }))

          // 상단 공통 에러는 지우거나 필요하면 그대로 두기
          setApiError(null)
          return
        }

        // ✅ 403: Turnstile 실패 → reset 강제
        if (response.status === 403) {
          gateRef.current?.reset()
        }

        // 그 외 상태코드에 대해서는 서버에서 온 error 메시지를 사용
        try {
          const data = await response.json()
          // console.log("data", data);

          if (data?.error) {
            errorMessage = data.error
          }
        } catch (error) {
          // JSON 파싱 실패 시 기본 메시지 유지
        }

        setApiError(errorMessage)
        return
      }

      // 성공 → 로그인 페이지로 이동
      // navigate('/login')

      setApiSuccess('회원가입이 완료되었습니다! 이메일 인증 후 로그인 가능합니다.')
    } catch (error) {
      setApiError('회원가입 중 오류가 발생했습니다')
    } finally {
      setLoading(false)
    }
    // setTimeout(() => {
    //   setLoading(false);
    //   window.REACT_APP_NAVIGATE("/login");
    // }, 1500);
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }

    // ✅ 이메일 입력 변경 시 localStorage 저장
    if (name === 'email') {
      try {
        const email = value.trim().toLowerCase()
        if (email) localStorage.setItem(LS_EMAIL_KEY, email)
        // else localStorage.removeItem(LS_EMAIL_KEY)
      } catch {}
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
              <i className="ri-cloud-line text-white text-2xl"></i>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Sense365 Cloud 가입</h1>
          {/* <p className="text-gray-600">Sense365 Cloud 가입</p> */}
          <RotatingMessage />
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="이름"
              name="name"
              type="text"
              placeholder="이름을 입력하세요"
              icon="ri-user-line"
              value={formData.name}
              onChange={handleChange}
              error={errors.name}
            />

            <Input
              label="이메일"
              name="email"
              type="email"
              placeholder="이메일을 입력하세요"
              icon="ri-mail-line"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
            />

            <Input
              label="비밀번호"
              name="password"
              type="password"
              placeholder="비밀번호를 입력하세요"
              icon="ri-lock-line"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
            />

            <Input
              label="비밀번호 확인"
              name="confirmPassword"
              type="password"
              placeholder="비밀번호를 다시 입력하세요"
              icon="ri-lock-line"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
            />

            <div className="flex items-start">
              <input
                type="checkbox"
                id="terms"
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                required
              />
              <label htmlFor="terms" className="ml-2 text-sm text-gray-600">
                <a href="#" className="text-blue-600 hover:text-blue-700 font-medium">
                  이용약관
                </a>{' '}
                및{' '}
                <a href="#" className="text-blue-600 hover:text-blue-700 font-medium">
                  개인정보처리방침
                </a>
                에 동의합니다
              </label>
            </div>

            {apiError && <p className="text-sm text-red-600">{apiError}</p>}
            {apiSuccess && <p className="text-sm text-green-600">{apiSuccess}</p>}

            <TurnstileGate ref={gateRef} action="signup" onTokenChange={setToken} />

            <Button type="submit" variant="primary" size="lg" className="w-full" loading={loading}>
              회원 가입
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              이미 계정이 있으신가요?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                로그인
              </Link>
            </p>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              {/* <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">또는</span>
              </div> */}
            </div>

            {/* <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                type="button"
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-google-fill text-lg mr-2"></i>
                Google
              </button>
              <button
                type="button"
                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-github-fill text-lg mr-2"></i>
                GitHub
              </button>
            </div> */}
          </div>
        </Card>
      </div>
    </div>
  )
}
